import { Icon, Typography } from '@repo/ui';
import { Badge, Card } from 'components';
import { CardErrorBoundaryWithSpinfinity } from 'components/Card/CardErrorBoundaryWithSpinfinity';
import ParticleSphere, {
  ResilienceLevel,
} from 'components/ParticleSphere/ParticleSphere';
import { evaluationToLevel } from 'helpers/gutResilience';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import useIsGutResilienceScoreEnabled from 'services/features/useIsGutResilienceScoreEnabled';
import useSubMetricEvaluations from 'services/insights/useSubMetricEvaluations';
import { RESILIENCE_CATEGORY_KEY } from 'views/Results/Insights/SubMetricDetail/GutResilienceScoreDetail';

import styles from './GutResilienceCard.module.css';

const LEVEL_LABEL: Record<ResilienceLevel, string> = {
  low: 'Low',
  borderline: 'Borderline',
  typical: 'Typical',
  high: 'High',
};

export const GutResilienceCard = () => {
  const { isEnabled } = useIsGutResilienceScoreEnabled();
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { data: metrics } = useSubMetricEvaluations();

  const resilienceSubMetric = metrics.find(
    subMetric => subMetric.category_key === RESILIENCE_CATEGORY_KEY,
  );

  if (!isEnabled || !resilienceSubMetric || !currentKitId) {
    return null;
  }

  const level = evaluationToLevel(resilienceSubMetric.evaluation_color);
  const detailHref = `/results/${currentKitId}/insights/all-insights/${resilienceSubMetric.gut_vaginal_health?.id}`;

  return (
    <CardErrorBoundaryWithSpinfinity cardName='GutResilienceCard'>
      <Card
        clickable={{
          href: detailHref,
          text: 'Resilience score',
          isExternal: false,
        }}
      >
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Typography variant='heading-s' className={styles.title}>
              Resilience score
            </Typography>
            <Badge title='NEW' state='new' />
          </div>
          <Icon name='chevronForward' size='s' className={styles.chevron} />
        </div>
        <div className={styles.visualization}>
          <ParticleSphere
            level={level}
            interactive={false}
            showHint={false}
            paused
            zoom={1.4}
            className={styles.particleSphere}
          />
          <div
            className={`${styles.levelBadge} ${styles[`levelBadge_${level}`]}`}
            aria-label={`Your resilience score is ${LEVEL_LABEL[level]}`}
          >
            <Typography variant='heading-l' className={styles.levelText}>
              {LEVEL_LABEL[level]}
            </Typography>
          </div>
        </div>
      </Card>
    </CardErrorBoundaryWithSpinfinity>
  );
};

export default GutResilienceCard;
